import { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';
import { FormProvider, Resolver, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import * as yup from 'yup';

import { Button } from '@/components/ui/button';
import { Dialog, DialogOverlay, DialogPortal } from '@/components/ui/dialog';
import { usePreferences } from '@/hooks/usePreferences';
import { useTemplates } from '@/hooks/useTemplates';
import { DEFAULT_FACTUALITY_SENSITIVITY } from '@/modules/aiAgents/constants';
import { popModal } from '@/redux/modals/actions';
import { setBrainId } from '@/redux/session/actions';
import { selectAccountSlug } from '@/redux/session/selectors';
import { trackEvent } from '@/segment/segment';
import { BrainLanguage, BrainType } from '@/shared/types/brain';
import { EventName } from '@/shared/types/segment';
import { aiAgentSchemas } from '@/util/validation/schemas/ai-agent-schemas';

import { ImportAIAgentForm } from './ImportAIAgentForm';
import { Step1 } from './Step1';
import { Step2 } from './Step2';

type Step1Form = { brain_type: BrainType };
type Step2Form = {
  name: string;
  language: BrainLanguage;
  provider: string;
  language_model_id: string;
};

export type Form = Step1Form & Partial<Step2Form>;

// Constants
const BASIC_STEPS = [
  { component: Step1, validationSchema: aiAgentSchemas.step1Schema },
  { component: Step2, validationSchema: aiAgentSchemas.step2Schema },
] as const;

export const ModalAIAgentCreate = ({
  defaultName,
  startWithImport = false,
}: {
  defaultName?: string;
  startWithImport?: boolean;
}) => {
  // Custom hooks
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { createBrainFromTemplateAsync } = useTemplates();
  const { userPreferences } = usePreferences();

  // Redux selectors
  const slug = useSelector(selectAccountSlug);

  // Local state
  const [step, setStep] = useState(startWithImport ? 2 : 1);

  const isLastStep = step === 2;
  const currentSchema = BASIC_STEPS[step - 1].validationSchema as yup.ObjectSchema<Form>;
  const CurrentStepComponent = BASIC_STEPS[step - 1].component;
  const userLanguage = userPreferences?.language || 'en';

  // RHF
  const methods = useForm<Form>({
    resolver: yupResolver(currentSchema) as Resolver<Form>,
    mode: 'onChange',
    defaultValues: startWithImport ? { brain_type: 'import' as BrainType } : {},
  });

  const {
    handleSubmit,
    trigger,
    formState: { isSubmitting, isValid },
    control,
  } = methods;

  const brainType = useWatch({ control, name: 'brain_type' });

  // Handlers
  const increaseStep = async () => {
    if (step === 2) {
      return;
    }
    const isStepValid = await trigger();
    if (isStepValid) {
      setStep((prev) => prev + 1);
    }
  };

  const decreaseStep = async () => {
    if (step === 1) {
      return;
    }

    setStep((prev) => prev - 1);
  };

  const closeModal = () => {
    dispatch(popModal());
  };

  const onSubmit = async (data: Form) => {
    trackEvent(EventName.ClickCreateAIAgent);

    // Remove the default language model if the value is "Default" or ''
    if (data.language_model_id === '' || data.language_model_id === 'Default') {
      data.language_model_id = null;
    }

    await createBrainFromTemplateAsync(
      {
        ...data,
        brain_id: 'playground',
        guidelines: { factuality_sensitivity: DEFAULT_FACTUALITY_SENSITIVITY },
      },
      {
        onSuccess: (brain) => {
          dispatch(setBrainId({ brainId: brain.brain_id }));
          dispatch(popModal());
          navigate(`/${slug}/ai-agents/${brain.brain_id}/overview`);
        },
      }
    );
  };

  // Dynamic image path based on the brain type and user language
  const getImagePath = () => {
    // Display default image for Import AI Agent option
    if (brainType === 'import') {
      return `/assets/aiAgents/modal/default_${userLanguage}.png`;
    }

    return `/assets/aiAgents/modal/${brainType ?? 'default'}_${userLanguage}.png`;
  };

  return (
    <Dialog open onOpenChange={(open) => !open && closeModal()}>
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          className="fixed top-1/2 left-1/2 z-50 flex h-[650px] max-h-[90vh] max-w-[1000px] min-w-[min(1000px,90vw)] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-lg bg-background shadow-lg data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 max-md:w-[660px]"
          aria-labelledby="create-ai-agent-modal-title"
          aria-describedby="create-ai-agent-modal-description"
        >
          <Button
            variant="ghost-neutral"
            size="icon-sm"
            className="absolute top-6 right-6 max-md:hidden"
            onClick={closeModal}
            aria-label={t('modals.close')}
          >
            <XIcon size={18} />
          </Button>

          <section className="m-0 flex flex-[0_0_60%] flex-col overflow-y-auto p-6 max-md:flex-[0_0_100%]">
            <header className="flex justify-between">
              <h1 className="text-xl font-semibold" id="create-ai-agent-modal-title">
                {t('ai_agents.modal.title')}
              </h1>
            </header>

            <FormProvider {...methods}>
              {brainType === 'import' && isLastStep ? (
                <ImportAIAgentForm setStep={setStep} />
              ) : (
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  id="create-ai-agent-modal-description"
                  className="flex h-full flex-col"
                >
                  <div className="mt-9">
                    {isLastStep ? <Step2 prefillName={defaultName} /> : <CurrentStepComponent />}
                  </div>

                  <footer className="mt-auto flex justify-end">
                    <div className="flex w-full gap-2 [&>:nth-last-child(2)]:ml-auto">
                      {step === 2 && (
                        <Button onClick={decreaseStep} variant="ghost" type="button">
                          {t('common.back')}
                        </Button>
                      )}

                      <Button
                        onClick={() => {
                          closeModal();
                        }}
                        variant="ghost-neutral"
                        type="button"
                      >
                        {t('common.cancel')}
                      </Button>

                      <Button
                        onClick={() => {
                          if (!isLastStep) {
                            increaseStep();
                          }
                        }}
                        disabled={!isValid}
                        isLoading={isSubmitting}
                        type={isLastStep ? 'submit' : 'button'}
                      >
                        {isLastStep ? t('common.create') : t('common.continue')}
                      </Button>
                    </div>
                  </footer>
                </form>
              )}
            </FormProvider>
          </section>

          <aside className="flex-[0_0_40%] overflow-hidden bg-foreground max-md:hidden">
            <img src={getImagePath()} className="h-full w-full object-cover" alt="" />
          </aside>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};
